import axios from 'axios';
import { PerplexitySupplierInfo } from '../types/supplier';
import { MaintenanceFrequencyType, MaintenanceAlertPriority } from '../types/maintenance';

const PERPLEXITY_API_KEY = import.meta.env.VITE_PERPLEXITY_API_KEY;

interface MaintenanceRecommendation {
  name: string;
  description: string;
  frequency: {
    type: MaintenanceFrequencyType;
    mileageValue?: number;
    timeValue?: number;
    timeUnit?: 'days' | 'months' | 'years';
  };
  priority: MaintenanceAlertPriority;
}

const perplexityApi = axios.create({
  baseURL: 'https://api.perplexity.ai',
  headers: {
    'Authorization': `Bearer ${PERPLEXITY_API_KEY}`,
    'Content-Type': 'application/json',
  },
});

const searchSupplier = async (
  name: string,
  location: string
): Promise<PerplexitySupplierInfo> => {
  try {
    console.log('Sending request to Perplexity API...');
    
    const response = await perplexityApi.post('/chat/completions', {
      model: "llama-3.1-sonar-small-128k-online",
      messages: [
        {
          role: "system",
          content: `You are a specialized business information assistant that focuses on finding accurate contact details. 
          Always return data in a clean JSON format with no additional text.
          Prioritize finding official business contact information from reliable sources.
          For phone numbers, prefer main business lines over secondary numbers.
          For email addresses, prefer general contact or info addresses over individual emails.
          Validate all contact information before including it.`
        },
        {
          role: "user",
          content: `Search for detailed business contact information for "${name}" in ${location}.
          Focus on finding:
          1. Exact physical address with proper formatting
          2. All available phone numbers (main line, service, support)
          3. Official email addresses
          4. Website URL
          5. Business hours
          6. Any social media presence
          7. Reviews and ratings if available
          
          Format the response as a JSON object with these fields:
          {
            "name": "exact business name",
            "address": "complete address",
            "phone": {
              "main": "primary number",
              "service": "service number (if available)",
              "support": "support number (if available)"
            },
            "email": {
              "main": "primary email",
              "support": "support email (if available)",
              "other": "additional email (if available)"
            },
            "website": "official website URL",
            "socialMedia": {
              "facebook": "URL",
              "linkedin": "URL",
              "twitter": "URL"
            },
            "hours": {
              "monday": {"open": "09:00", "close": "18:00"}
            },
            "rating": number,
            "reviews": number,
            "lastVerified": "timestamp"
          }`
        }
      ],
      temperature: 0.1,
      top_p: 0.9,
      max_tokens: 2048,
      stream: false,
      presence_penalty: 0,
      frequency_penalty: 1
    });

    console.log('Received response:', response.data);

    const answer = response.data.choices[0].message.content;
    
    // Try to extract and clean the JSON from the response text
    let jsonData;
    try {
      // First, try to find a JSON object in the response
      const jsonMatch = answer.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.error('No JSON found in response:', answer);
        throw new Error('No JSON found in response');
      }

      // Clean up the JSON string
      let jsonString = jsonMatch[0]
        .replace(/,(\s*[}\]])/g, '$1')
        .replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, '')
        .replace(/\s*\/\/.*/g, '')
        .trim();

      try {
        jsonData = JSON.parse(jsonString);
      } catch (firstParseError) {
        console.error('First parse attempt failed:', firstParseError);
        console.log('Attempting to fix common JSON issues...');

        jsonString = jsonString
          .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3')
          .replace(/'/g, '"')
          .replace(/[\u0000-\u001F]+/g, '');

        try {
          jsonData = JSON.parse(jsonString);
        } catch (secondParseError) {
          console.error('Second parse attempt failed:', secondParseError);
          console.error('Raw response:', answer);
          console.error('Cleaned JSON string:', jsonString);
          throw new Error('Failed to parse response data');
        }
      }
    } catch (error) {
      console.error('Error processing Perplexity response:', error);
      console.error('Raw response:', answer);
      throw new Error(error instanceof Error ? error.message : 'Invalid response format');
    }

    const supplierInfo: PerplexitySupplierInfo = {
      name: jsonData.name || name,
      address: jsonData.address || '',
      phone: typeof jsonData.phone === 'object' 
        ? jsonData.phone.main || jsonData.phone.service || jsonData.phone.support || ''
        : jsonData.phone || '',
      email: typeof jsonData.email === 'object'
        ? jsonData.email.main || jsonData.email.support || jsonData.email.other || ''
        : jsonData.email || '',
      website: jsonData.website || '',
      hours: typeof jsonData.hours === 'object' ? jsonData.hours : {},
      rating: typeof jsonData.rating === 'number' ? jsonData.rating : undefined,
      reviews: typeof jsonData.reviews === 'number' ? jsonData.reviews : undefined,
      socialMedia: typeof jsonData.socialMedia === 'object' ? jsonData.socialMedia : {},
      lastVerified: jsonData.lastVerified || new Date().toISOString()
    };

    return supplierInfo;
  } catch (error) {
    console.error('Error searching supplier with Perplexity:', error);
    if (axios.isAxiosError(error)) {
      console.error('Axios error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    }
    throw error;
  }
};

const getMaintenanceRecommendations = async (
  make: string,
  model: string,
  year: number
): Promise<MaintenanceRecommendation[]> => {
  try {
    const response = await perplexityApi.post('/chat/completions', {
      model: "llama-3.1-sonar-small-128k-online",
      messages: [
        {
          role: "system",
          content: `You are a vehicle maintenance expert that provides detailed maintenance schedules. 
          Return data in a clean JSON array format with no additional text.
          Focus on manufacturer-recommended maintenance tasks.
          Include maintenance items that can be based on:
          - Mileage only (type: "mileage")
          - Time only (type: "time")
          - Both mileage and time (type: "time_or_mileage")`
        },
        {
          role: "user",
          content: `Generate a maintenance schedule for a ${year} ${make} ${model}.
          Include:
          1. Regular service intervals
          2. Component replacements
          3. Safety inspections
          4. Fluid changes
          
          Format each task as:
          {
            "name": "task name",
            "description": "detailed description",
            "frequency": {
              "type": "mileage" | "time" | "time_or_mileage",
              "mileageValue": number (for type mileage or time_or_mileage),
              "timeValue": number (for type time or time_or_mileage),
              "timeUnit": "days" | "months" | "years" (for type time or time_or_mileage)
            },
            "priority": "critical" | "moderate" | "low"
          }
          
          Examples:
          - Oil change: type "mileage", mileageValue 10000
          - Brake fluid: type "time", timeValue 2, timeUnit "years"
          - Timing belt: type "time_or_mileage", mileageValue 100000, timeValue 5, timeUnit "years"`
        }
      ],
      temperature: 0.1,
      max_tokens: 2048
    });

    const answer = response.data.choices[0].message.content;
    let recommendations: MaintenanceRecommendation[];

    try {
      const jsonMatch = answer.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        throw new Error('No JSON array found in response');
      }

      recommendations = JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.error('Error parsing maintenance recommendations:', error);
      throw new Error('Failed to parse maintenance recommendations');
    }

    return recommendations;
  } catch (error) {
    console.error('Error getting maintenance recommendations:', error);
    throw error;
  }
};

export const perplexityService = {
  searchSupplier,
  getMaintenanceRecommendations
};
